/**
 * Camada de acesso do Sistema de Templates de Tenant.
 *
 * Todo acesso a dados passa por RPCs `SECURITY DEFINER` — nunca há SELECT
 * direto em tabelas a partir do navegador.
 *
 * @version 1.5.0
 * RELEASE: 2026-09-08 v1.5.0 - Prévia dos itens de cada módulo (exportação),
 *          leitura do conteúdo do pacote (importação) e envio da seleção
 *          granular junto com o responsável do plano.
 * RELEASE: 2026-09-08 v1.4.0 - Download do pacote gerado e acionamento manual
 *          do processamento.

 * RELEASE: 2026-09-08 v1.3.0 - Fase 5: indicadores de acompanhamento do módulo.
 * RELEASE: 2026-09-08 v1.2.0 - Mapeamento de pessoas: lista de responsáveis da
 *          conta e escolha do responsável padrão da implantação.
 * RELEASE: 2026-09-08 v1.1.0 - Leitura da simulação, histórico de execuções,
 *          pendências pós-implantação e reprocessamento.
 * RELEASE: 2026-09-08 v1.0.0 - Versão inicial (Fase 1 do plano).
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { TEMPLATE_PACKAGE_MAX_SIZE_BYTES } from './constants';
import type { TemplateModuleKey } from './constants';

/** Bucket privado onde ficam os pacotes `.uaizytemplate`. */
export const TEMPLATE_PACKAGES_BUCKET = 'template-packages';

/** Item da biblioteca de templates exibido na tela central. */
export interface TemplatePackageListItem {
  id: string;
  name: string;
  description: string | null;
  segment: string | null;
  tags: string[] | null;
  is_archived: boolean;
  current_version_id: string | null;
  current_version_number: number | null;
  current_version_status: string | null;
  versions_count: number;
  created_at: string;
  updated_at: string;
}

/** Resumo de uma execução (exportar, importar, aplicar, desfazer). */
export interface TemplateJobDetail {
  id: string;
  kind: string;
  status: string;
  progress_percent: number;
  counters: Record<string, unknown>;
  error_payload: Record<string, unknown> | null;
  package_version_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  steps: Array<{
    module_key: string;
    step_index: number;
    status: string;
    retry_count: number;
    error_payload: Record<string, unknown> | null;
  }>;
}

/** Lista os templates da conta atual. */
export async function listTemplatePackages(
  includeArchived = false,
): Promise<TemplatePackageListItem[]> {
  const { data, error } = await supabase.rpc('get_tenant_template_packages', {
    p_include_archived: includeArchived,
  });

  if (error) {
    logger.error('[templates] falha ao listar pacotes', error.message);
    throw error;
  }

  return (data ?? []) as TemplatePackageListItem[];
}

/** Lista as versões de um template. */
export async function listTemplatePackageVersions(packageId: string) {
  const { data, error } = await supabase.rpc('get_tenant_template_package_versions', {
    p_package_id: packageId,
  });

  if (error) {
    logger.error('[templates] falha ao listar versões', error.message);
    throw error;
  }

  return data ?? [];
}

/** Busca o detalhe de uma execução, incluindo os passos por módulo. */
export async function getTemplateJob(jobId: string): Promise<TemplateJobDetail> {
  const { data, error } = await supabase.rpc('get_tenant_template_job', {
    p_job_id: jobId,
  });

  if (error) {
    logger.error('[templates] falha ao consultar execução', error.message);
    throw error;
  }

  return data as unknown as TemplateJobDetail;
}

/**
 * Seleção granular do que será exportado.
 * Formato: { crm: { products: ['Nome A'], pipelines: null } }
 *  - coleção com null  -> leva a coleção inteira
 *  - coleção com lista -> leva apenas os itens com aquele rótulo
 *  - coleção ausente   -> não é exportada
 */
export type TemplateSelection = Record<
  string,
  Record<string, string[] | null | Record<string, string>>
>;


/** Parâmetros do assistente de exportação. */
export interface CreateExportInput {
  name: string;
  modules: TemplateModuleKey[];
  description?: string | null;
  segment?: string | null;
  tags?: string[];
  idempotencyKey?: string;
  /** Opcional: quando ausente, exporta tudo dos módulos escolhidos. */
  selection?: TemplateSelection | null;
}

/** Cria a execução de exportação de um novo template. */
export async function createTemplateExportJob(input: CreateExportInput) {
  const { data, error } = await supabase.rpc('create_tenant_template_export_job', {
    p_name: input.name,
    p_modules: input.modules,
    p_description: input.description ?? null,
    p_segment: input.segment ?? null,
    p_tags: input.tags ?? [],
    p_idempotency_key: input.idempotencyKey ?? null,
    p_selection: (input.selection ?? null) as never,
  });

  if (error) {
    logger.error('[templates] falha ao criar exportação', error.message);
    throw error;
  }

  return data as unknown as {
    package_id: string;
    package_version_id: string;
    version_number: number;
    job_id: string;
  };
}

/**
 * Envia o arquivo `.uaizytemplate` para o bucket privado e abre a execução
 * de importação. O caminho sempre começa pelo id da conta, o que é validado
 * no servidor.
 */
export async function uploadTemplatePackage(tenantId: string, file: File) {
  if (file.size > TEMPLATE_PACKAGE_MAX_SIZE_BYTES) {
    throw new Error('O arquivo deve ter até 50 MB.');
  }

  const path = `${tenantId}/${crypto.randomUUID()}-${file.name}`;

  const { error: uploadError } = await supabase.storage
    .from(TEMPLATE_PACKAGES_BUCKET)
    .upload(path, file, { upsert: false, contentType: 'application/json' });

  if (uploadError) {
    logger.error('[templates] falha no envio do arquivo', uploadError.message);
    throw uploadError;
  }

  const { data, error } = await supabase.rpc('create_tenant_template_import_job', {
    p_name: file.name.replace(/\.[^.]+$/, ''),
    p_storage_path: path,
    p_file_size_bytes: file.size,
    p_checksum: null,
  });

  if (error) {
    logger.error('[templates] falha ao registrar importação', error.message);
    throw error;
  }

  return data as unknown as {
    package_id: string;
    package_version_id: string;
    job_id: string;
  };
}

/** Aprova o plano da simulação e dispara a aplicação. */
export async function approveTemplatePlan(planId: string, idempotencyKey?: string) {
  const { data, error } = await supabase.rpc('approve_tenant_template_plan', {
    p_plan_id: planId,
    p_idempotency_key: idempotencyKey ?? null,
  });

  if (error) {
    logger.error('[templates] falha ao aprovar plano', error.message);
    throw error;
  }

  return data as unknown as string;
}

/** Solicita o desfazimento de uma aplicação já finalizada. */
export async function requestTemplateRollback(applyJobId: string) {
  const { data, error } = await supabase.rpc('request_tenant_template_rollback', {
    p_apply_job_id: applyJobId,
  });

  if (error) {
    logger.error('[templates] falha ao solicitar reversão', error.message);
    throw error;
  }

  return data as unknown as string;
}

/** Resumo da simulação gerada após a verificação do arquivo. */
export interface TemplatePlanDetail {
  id: string;
  status: string;
  plan_hash: string;
  expires_at: string;
  summary: {
    modules?: Array<{ key: string; count: number; conflicts: number }>;
    total_items?: number;
    total_conflicts?: number;
    unresolved_references?: number;
  };
  decisions: Record<string, unknown>;
}

/** Busca a simulação (plano) de uma versão de template. */
export async function getTemplatePlan(
  packageVersionId: string,
): Promise<TemplatePlanDetail | null> {
  const { data, error } = await supabase.rpc('get_tenant_template_plan', {
    p_package_version_id: packageVersionId,
  });

  if (error) {
    logger.error('[templates] falha ao consultar simulação', error.message);
    throw error;
  }

  return (data as unknown as TemplatePlanDetail | null) ?? null;
}

/** Linha do histórico de execuções. */
export interface TemplateJobListItem {
  id: string;
  kind: string;
  status: string;
  progress_percent: number;
  attempt_count: number;
  counters: Record<string, unknown>;
  error_payload: Record<string, unknown> | null;
  package_name: string | null;
  version_number: number | null;
  package_version_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

/** Lista as execuções recentes da conta. */
export async function listTemplateJobs(limit = 20): Promise<TemplateJobListItem[]> {
  const { data, error } = await supabase.rpc('get_tenant_template_jobs', { p_limit: limit });

  if (error) {
    logger.error('[templates] falha ao listar execuções', error.message);
    throw error;
  }

  return (data ?? []) as TemplateJobListItem[];
}

/** Item da lista de pendências pós-implantação. */
export interface TemplatePendingAction {
  origem: string;
  module_key: string | null;
  descricao: string;
  detalhe: Record<string, unknown> | null;
}

/** Lista o que ficou pendente depois de uma execução. */
export async function listTemplatePendingActions(
  jobId: string,
): Promise<TemplatePendingAction[]> {
  const { data, error } = await supabase.rpc('get_tenant_template_pending_actions', {
    p_job_id: jobId,
  });

  if (error) {
    logger.error('[templates] falha ao listar pendências', error.message);
    throw error;
  }

  return (data ?? []) as TemplatePendingAction[];
}

/** Recoloca uma execução com erro na fila (retomada). */
export async function retryTemplateJob(jobId: string) {
  const { error } = await supabase.rpc('retry_tenant_template_job', { p_job_id: jobId });

  if (error) {
    logger.error('[templates] falha ao reprocessar execução', error.message);
    throw error;
  }
}

/** Pessoa da conta de destino, usada no mapeamento de responsáveis. */
export interface TemplateTenantMember {
  user_id: string;
  full_name: string | null;
  email: string | null;
}

/** Lista as pessoas ativas da conta atual (mapeamento de responsáveis). */
export async function listTemplateTenantMembers(
  tenantId: string,
): Promise<TemplateTenantMember[]> {
  const { data, error } = await (supabase.rpc as any)('get_tenant_members_safe', {
    p_tenant_hint: tenantId,
    p_only_active: true,
  });

  if (error) {
    logger.error('[templates] falha ao listar pessoas da conta', error.message);
    throw error;
  }

  return ((data ?? []) as any[]).map((m) => ({
    user_id: m.user_id,
    full_name: m.full_name ?? null,
    email: m.email ?? null,
  }));
}

/**
 * Define o responsável padrão da implantação (mapeamento de pessoas) e,
 * opcionalmente, a seleção granular de itens que serão aplicados.
 * Vale para cadastros que exigem um dono na conta de destino, como agendas.
 */
export async function setTemplatePlanOwner(
  planId: string,
  ownerUserId: string,
  selection?: TemplateSelection | null,
) {
  const { error } = await (supabase.rpc as any)('set_tenant_template_plan_owner', {
    p_plan_id: planId,
    p_owner_user_id: ownerUserId,
    p_selection: selection ?? null,
  });

  if (error) {
    logger.error('[templates] falha ao definir responsável padrão', error.message);
    throw error;
  }
}

/** Coleção de um módulo com os rótulos dos itens disponíveis. */
export interface TemplateCollectionItems {
  /** Nome interno da coleção dentro do módulo (ex.: products). */
  collection: string;
  /** Rótulos legíveis dos itens encontrados. */
  labels: string[];
  /**
   * Itens brutos, na MESMA ordem dos rótulos. Usado para filtrar a seleção
   * por vínculo (ex.: só o que pertence a uma workspace). Opcional para
   * manter compatibilidade com quem só usa `labels`.
   */
  items?: unknown[];
}


/** Campos aceitos como rótulo legível de um item do pacote. */
const CAMPOS_ROTULO = ['name', 'title', 'label', 'code', 'slug', 'key'] as const;

/** Campos de contexto usados como prefixo do rótulo (evita nomes repetidos). */
const CAMPOS_CONTEXTO = [
  'category_ref',
  'pipeline_ref',
  'program_ref',
  'workspace_ref',
  'parent_ref',
] as const;

/** Monta o rótulo legível de um item, com contexto quando existir. */
function montarRotulo(item: unknown, indice: number): string {
  if (!item || typeof item !== 'object') return `Item ${indice + 1}`;
  const registro = item as Record<string, unknown>;

  let base: string | null = null;
  for (const campo of CAMPOS_ROTULO) {
    const bruto = registro[campo];
    if (typeof bruto === 'string' && bruto.trim()) {
      base = bruto.trim();
      break;
    }
  }
  if (!base) return `Item ${indice + 1}`;

  for (const campo of CAMPOS_CONTEXTO) {
    const bruto = registro[campo];
    if (typeof bruto === 'string' && bruto.trim()) {
      return `${bruto.trim()} › ${base}`;
    }
  }

  return base;
}

/**
 * Converte o JSON de um módulo (exportado ou vindo do pacote) na lista de
 * coleções com os rótulos dos seus itens. Coleções vazias são descartadas.
 *
 * Aceita tanto o envelope do adaptador (`{ supported, items: {...} }`) quanto
 * um conteúdo já "achatado" (`{ products: [...] }`).
 */
export function extrairColecoes(conteudo: unknown): TemplateCollectionItems[] {
  if (!conteudo || typeof conteudo !== 'object') return [];

  const bruto = conteudo as Record<string, unknown>;
  // O adaptador devolve as coleções dentro de `items`
  const raiz =
    bruto.items && typeof bruto.items === 'object' && !Array.isArray(bruto.items)
      ? (bruto.items as Record<string, unknown>)
      : bruto;

  const resultado: TemplateCollectionItems[] = [];

  for (const [colecao, valor] of Object.entries(raiz)) {
    if (!Array.isArray(valor) || valor.length === 0) continue;

    const labels = valor.map((item, indice) => montarRotulo(item, indice));

    if (labels.length > 0) {
      // `items` acompanha `labels` na mesma ordem (usado na seleção por escopo)
      resultado.push({ collection: colecao, labels, items: valor });
    }

  }

  return resultado.sort((a, b) => a.collection.localeCompare(b.collection));
}


/**
 * Prévia dos itens de um módulo desta conta, usada para montar a árvore de
 * seleção da exportação. Reaproveita a rotina de exportação (sem gravar nada).
 */
export async function previewTemplateModuleItems(
  moduleKey: string,
): Promise<TemplateCollectionItems[]> {
  const { data, error } = await (supabase.rpc as any)('export_tenant_template_module', {
    p_module_key: moduleKey,
  });

  if (error) {
    logger.error('[templates] falha ao pré-visualizar módulo', error.message);
    throw error;
  }

  return extrairColecoes(data);
}

/**
 * Lê o conteúdo do arquivo `.uaizytemplate` de uma versão, para listar o que
 * o pacote traz por módulo antes de aplicar.
 */
export async function fetchTemplateVersionContent(
  versionId: string,
): Promise<Record<string, TemplateCollectionItems[]>> {
  const { data, error } = await (supabase.rpc as any)('get_tenant_template_version_file', {
    p_version_id: versionId,
  });

  if (error) {
    logger.error('[templates] falha ao localizar o arquivo do template', error.message);
    throw error;
  }

  const info = (data ?? {}) as { storage_path?: string };
  if (!info.storage_path) return {};

  const { data: assinado, error: erroAssinatura } = await supabase.storage
    .from(TEMPLATE_PACKAGES_BUCKET)
    .createSignedUrl(info.storage_path, 300);

  if (erroAssinatura || !assinado?.signedUrl) {
    logger.error('[templates] falha ao abrir o arquivo do pacote', erroAssinatura?.message ?? '');
    throw erroAssinatura ?? new Error('Não foi possível abrir o arquivo do pacote.');
  }

  const resposta = await fetch(assinado.signedUrl);
  if (!resposta.ok) {
    throw new Error('Não foi possível ler o arquivo do pacote.');
  }

  const envelope = (await resposta.json()) as Record<string, unknown>;
  const modulos = (envelope.modules ?? envelope.data ?? {}) as Record<string, unknown>;
  const saida: Record<string, TemplateCollectionItems[]> = {};

  for (const [chave, conteudo] of Object.entries(modulos)) {
    const colecoes = extrairColecoes(conteudo);
    if (colecoes.length > 0) saida[chave] = colecoes;
  }

  return saida;
}



/** Indicadores de acompanhamento do módulo (observabilidade). */
export interface TemplateMetrics {
  packages: number;
  versions: number;
  jobs_pending: number;
  jobs_running: number;
  jobs_completed_24h: number;
  jobs_failed_24h: number;
  avg_duration_seconds: number;
  plans_pending: number;
  dead_letters: number;
}

/** Lê os indicadores do módulo para a conta atual. */
export async function getTemplateMetrics(): Promise<TemplateMetrics> {
  const { data, error } = await (supabase.rpc as any)('get_tenant_template_metrics', {});

  if (error) {
    logger.error('[templates] falha ao ler indicadores', error.message);
    throw error;
  }

  const bruto = (data ?? {}) as Record<string, unknown>;

  return {
    packages: Number(bruto.packages ?? 0),
    versions: Number(bruto.versions ?? 0),
    jobs_pending: Number(bruto.jobs_pending ?? 0),
    jobs_running: Number(bruto.jobs_running ?? 0),
    jobs_completed_24h: Number(bruto.jobs_completed_24h ?? 0),
    jobs_failed_24h: Number(bruto.jobs_failed_24h ?? 0),
    avg_duration_seconds: Number(bruto.avg_duration_seconds ?? 0),
    plans_pending: Number(bruto.plans_pending ?? 0),
    dead_letters: Number(bruto.dead_letters ?? 0),
  };
}

/**
 * Baixa o arquivo `.uaizytemplate` de uma versão.
 *
 * O caminho é resolvido no servidor (com checagem de conta, permissão e
 * auditoria) e o link temporário é gerado a partir do bucket privado.
 * Validade do link: 5 minutos.
 */
export async function downloadTemplateVersion(versionId: string) {
  const { data, error } = await (supabase.rpc as any)('get_tenant_template_version_file', {
    p_version_id: versionId,
  });

  if (error) {
    logger.error('[templates] falha ao localizar o arquivo do template', error.message);
    throw error;
  }

  const info = (data ?? {}) as { storage_path?: string; suggested_filename?: string };

  if (!info.storage_path) {
    throw new Error('O arquivo desta versão ainda não está pronto.');
  }

  const { data: assinado, error: erroAssinatura } = await supabase.storage
    .from(TEMPLATE_PACKAGES_BUCKET)
    .createSignedUrl(info.storage_path, 300, {
      download: info.suggested_filename ?? 'template.uaizytemplate',
    });

  if (erroAssinatura || !assinado?.signedUrl) {
    logger.error('[templates] falha ao gerar link do arquivo', erroAssinatura?.message ?? 'sem url');
    throw erroAssinatura ?? new Error('Não foi possível gerar o link do arquivo.');
  }

  // Dispara o download sem sair da tela
  const ancora = document.createElement('a');
  ancora.href = assinado.signedUrl;
  ancora.download = info.suggested_filename ?? 'template.uaizytemplate';
  document.body.appendChild(ancora);
  ancora.click();
  ancora.remove();

  return info.suggested_filename ?? 'template.uaizytemplate';
}

/**
 * Aciona o processador de templates imediatamente, sem esperar a rotina
 * automática (que roda a cada 1 minuto). Só processa execuções pendentes.
 */
export async function runTemplateWorkerNow(): Promise<{ processed: number }> {
  const { data, error } = await supabase.functions.invoke('template-worker', { body: {} });

  if (error) {
    logger.error('[templates] falha ao acionar o processamento', error.message);
    throw error;
  }

  const bruto = (data ?? {}) as Record<string, unknown>;

  return { processed: Number(bruto.processados ?? 0) };
}
